import { useState } from 'react';
import { utilApi } from '../../api';
import Select from './Select';

type Props = {
  /** Pre-fill the "from" date (YYYY-MM-DD). Defaults to today. */
  defaultFrom?: string;
  /** Default offset in days. */
  defaultOffset?: number;
  /** Called when the user clicks "Use this date". Optional — hide the button if omitted. */
  onUse?: (iso: string) => void;
};

// Common SA procedural deadlines (Uniform Rules + Magistrates' Court Rules).
// Kept short for quick pick; advocates can override the offset manually.
const PRESETS: Array<{ label: string; offset: number; mode: 'court' | 'calendar'; note?: string }> = [
  { label: 'NOITD (URL 19)',               offset: 10, mode: 'court',    note: 'Notice of intention to defend — 10 court days from service of summons.' },
  { label: 'Plea (URL 22)',                offset: 20, mode: 'court',    note: '20 court days after NOITD.' },
  { label: 'Answering affidavit (URL 6(5)(d))', offset: 15, mode: 'court', note: '15 court days from notice of opposition.' },
  { label: 'Replying affidavit',           offset: 10, mode: 'court',    note: '10 court days from answering affidavit.' },
  { label: 'Discovery (URL 35)',           offset: 20, mode: 'court',    note: '20 court days after close of pleadings.' },
  { label: 'Notice to set down (URL 31)',  offset: 10, mode: 'court',    note: '10 court days.' },
  { label: 'Rule 43(2) opposition',        offset: 10, mode: 'court',    note: '10 court days.' },
  { label: 'Magistrate plea (MCR 17)',     offset: 10, mode: 'court' },
  { label: 'Appeal petition (CC/SCA)',     offset: 15, mode: 'calendar' },
];

export default function DeadlineCalculator({ defaultFrom, defaultOffset = 10, onUse }: Props) {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(defaultFrom || today);
  const [offset, setOffset] = useState(defaultOffset);
  const [mode, setMode] = useState<'court' | 'calendar'>('court');
  const [presetIdx, setPresetIdx] = useState<string>('');
  const [result, setResult] = useState<{ due_date: string; weekday: string; is_court_day: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const compute = async () => {
    setLoading(true); setError(null);
    try {
      const r = await utilApi.computeDeadline({ from, offset_days: Number(offset), mode });
      setResult(r);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };

  const applyPreset = (idx: string) => {
    setPresetIdx(idx);
    if (idx === '') return;
    const p = PRESETS[Number(idx)];
    if (!p) return;
    setOffset(p.offset);
    setMode(p.mode);
  };

  const preset = presetIdx !== '' ? PRESETS[Number(presetIdx)] : null;

  return (
    <div className="card stack">
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div className="grow">
          <label>Preset (SA rules)</label>
          <Select
            value={presetIdx}
            onChange={applyPreset}
            options={[
              { value: '', label: 'Custom — pick offset manually' },
              ...PRESETS.map((p, i) => ({
                value: String(i),
                label: p.label,
                hint: `${p.offset} ${p.mode === 'court' ? 'court' : 'cal'} days`,
              })),
            ]}
            searchPlaceholder="Search Uniform Rules / MC Rules…"
          />
        </div>
      </div>
      {preset?.note && (
        <div className="muted" style={{ fontSize: 12 }}>{preset.note}</div>
      )}

      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label>From</label>
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setResult(null); }} />
        </div>
        <div style={{ width: 120 }}>
          <label>Offset</label>
          <input
            type="number"
            value={offset}
            onChange={(e) => { setOffset(Number(e.target.value)); setResult(null); }}
          />
        </div>
        <div style={{ width: 180 }}>
          <label>Day type</label>
          <Select
            value={mode}
            onChange={(v) => { setMode(v as 'court' | 'calendar'); setResult(null); }}
            options={[
              { value: 'court',    label: 'Court days',     hint: 'skips weekends + SA holidays + recess' },
              { value: 'calendar', label: 'Calendar days',  hint: 'every day counted' },
            ]}
          />
        </div>
        <button className="primary" onClick={compute} disabled={loading}>
          {loading ? 'Computing…' : 'Compute'}
        </button>
      </div>

      {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

      {result && (
        <div style={{
          border: `1px solid ${result.is_court_day ? 'var(--color-accent)' : 'var(--color-danger)'}`,
          borderRadius: 'var(--radius)',
          padding: 14,
          background: result.is_court_day ? 'var(--color-accent-soft)' : 'var(--color-danger-soft)',
        }}>
          <div style={{ fontSize: 22, fontWeight: 600, color: result.is_court_day ? 'var(--color-accent)' : 'var(--color-danger)' }}>
            {result.weekday}, {formatLong(result.due_date)}
          </div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            {offset} {mode === 'court' ? 'court days' : 'calendar days'} from {formatLong(from)}
            {!result.is_court_day && mode === 'court' && ' — note: this lands on a non-court day.'}
          </div>
          {onUse && (
            <button className="primary" style={{ marginTop: 10 }} onClick={() => onUse(result.due_date)}>
              Use this date
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function formatLong(iso: string) {
  try {
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString('en-ZA', { weekday: undefined, day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
  } catch { return iso; }
}
